import * as Sentry from '@sentry/browser';

declare const Cookies: {
    get(name: string): string | undefined;
};

export class ApiFetchError extends Error {
    readonly status: number;
    readonly response: Response;

    constructor(message: string, response: Response) {
        super(message);
        this.name = 'ApiFetchError';
        this.status = response.status;
        this.response = response;
    }
}

/**
 * Returns Authorization header if user token is set
 *
 * @return {object} Empty object if no token is set, else X-CSRFToken header object
 */
const getAuthorizationHeader = (): Record<string, string> => {
    const token = Cookies.get('csrftoken');
    if (!token) {
        return {};
    }
    return {
        'X-CSRFToken': token,
    };
};

const STATUS_MESSAGES: Record<number, string> = {
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
};

/**
 * Checks if a network request came back fine, and throws an ApiFetchError if not.
 *
 * @param  {object} response  A response from a network request
 * @return {object}           The response, if ok
 */
const validateResponseStatus = (response: Response): Response => {
    if (response.ok) {
        return response;
    }

    const knownMessage = STATUS_MESSAGES[response.status];
    if (knownMessage) {
        throw new ApiFetchError(knownMessage, response);
    }

    const statusText = response.statusText?.trim() || 'No status text';
    const url = response.url || 'Unknown URL';
    const error = new ApiFetchError(`HTTP ${response.status} - ${statusText} (${url})`, response);

    Sentry.captureException(error, {
        extra: {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
        },
    });

    throw error;
};

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 * @return {object|null}     The parsed JSON from the request
 */
const parseResponseJSON = (response: Response) => {
    if (response.status === 204 || response.status === 205) return null;
    if (response.headers?.get('content-length') === '0') return null;

    return response.json();
};

/**
 * Parses the text response returned by a network request
 *
 * @param  {object} response Response stream
 * @return {object}          Parsed response
 */
const parseResponseText = (response: Response) => response.text();

/**
 * Requests a URL, returning a promise
 * Catching an error should be handled by caller
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @return {object}           The response data
 */
export const fetchRequest = (url: string, options: RequestInit = {}) => {
    const authHeader = getAuthorizationHeader();
    // Skip the JSON Content-Type default when the body is FormData so the browser
    // can set `multipart/form-data; boundary=...` itself. Caller headers are spread
    // last so they augment (not replace) the defaults — keeps CSRF intact.
    const isFormData = options.body instanceof FormData;
    const { headers: callerHeaders, ...restOptions } = options;
    const extendedOptions = {
        method: 'GET',
        ...restOptions,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...authHeader,
            ...(callerHeaders ?? {}),
        },
    };

    return fetch(url, extendedOptions)
        .then(validateResponseStatus)
        .then(parseResponseJSON)
        .catch((err) => {
            // Intentional aborts (caller aborted via AbortSignal) aren't failures —
            // rethrow quietly so callers can handle them without console noise.
            if (err?.name !== 'AbortError') {
                // eslint-disable-next-line no-console
                console.error('Request failed:', err);
            }
            throw err;
        });
};

const apiFetchRequest = (path: string, options?: RequestInit) => fetchRequest(`/api/${path}`, options);

/**
 * Fetch HTML from url provided
 *
 * @param {string} url
 * @param {object} options
 */
export const documentFetchRequest = (url: string, options: RequestInit = {}) => {
    const authHeader = getAuthorizationHeader();
    const extendedOptions = {
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            ...authHeader,
        },
        method: options.method || 'GET',
        ...options,
    };

    return fetch(url, extendedOptions)
        .then(validateResponseStatus)
        .then(parseResponseText)
        .catch((err) => {
            // Intentional aborts aren't failures — skip the log.
            if (err?.name !== 'AbortError') {
                // eslint-disable-next-line no-console
                console.error('Request failed:', err);
            }
        });
};

/**
 * Create an URL by adding optional parameters to a URL base.
 *
 * @param {string} base
 * @param {key: boolean|number|string} params
 * @returns {string}
 */
export const buildURL = (base: string, params: Record<string, boolean | number | string> = {}) => {
    if (typeof params !== 'object' || Object.keys(params).length === 0) {
        return base;
    }

    const pairs: string[] = [];

    const queryString = base.split('?')[1];
    if (queryString) {
        queryString.split('&').forEach((x) => pairs.push(x));
    }

    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== undefined) {
            pairs.push(`${key}=${value.toString()}`);
        }
    });

    return `${base}?${pairs.join('&')}`;
};

export default apiFetchRequest;
